import React, { useState, useEffect, useCallback } from "react";
import { Layout, Select, Modal, Form, TimePicker, Upload, Button, Tag, Divider, Typography } from "antd";
import { UploadOutlined, CheckCircleFilled, ExclamationCircleFilled, CalendarOutlined, ClockCircleOutlined, UserOutlined, FileTextOutlined, CloseCircleFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";


const { Content } = Layout;
const { Option } = Select;

/* ─── Paleta de Status ─────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  PRESENTE:           { bg: "#D1FAE5", color: "#065F46", dot: "#10B981", label: "Presente" },
  ATRASO:             { bg: "#FFF7ED", color: "#9A3412", dot: "#F97316", label: "Atraso" },
  FALTA:              { bg: "#FEE2E2", color: "#991B1B", dot: "#EF4444", label: "Falta" },
  "FALTA JUSTIFICADA":{ bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B", label: "Justificada" },
  "FÉRIAS":           { bg: "#DBEAFE", color: "#1E3A8A", dot: "#3B82F6", label: "Férias" },
  FERIADO:            { bg: "#EDE9FE", color: "#4C1D95", dot: "#8B5CF6", label: "Feriado" },
  "FINAL DE SEMANA":  { bg: "#F3F4F6", color: "#6B7280", dot: "#D1D5DB", label: "F. Semana" },
};

/* ─── Status das Faltas ────────────────────────────────────────────────────── */
const FALTA_STATUS = {
  S: {
    label: "Aceite",
    bg: "#ECFDF5",
    color: "#065F46",
    border: "#A7F3D0",
    dot: "#10B981",
    icon: "✓",
    badgeBg: "#D1FAE5",
    cardBorder: "#10B981",
  },
  N: {
    label: "Rejeitado",
    bg: "#FFF1F2",
    color: "#9F1239",
    border: "#FECDD3",
    dot: "#F43F5E",
    icon: "✕",
    badgeBg: "#FFE4E6",
    cardBorder: "#F43F5E",
  },
  P: {
    label: "Pendente",
    bg: "#FFFBEB",
    color: "#92400E",
    border: "#FDE68A",
    dot: "#F59E0B",
    icon: "⏳",
    badgeBg: "#FEF3C7",
    cardBorder: "#F59E0B",
  },
};

const getFaltaStatus = (justificado) => {
  if (justificado === "S") return FALTA_STATUS.S;
  if (justificado === "N") return FALTA_STATUS.N;
  return FALTA_STATUS.P;
};

const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const MESES_CURTOS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const DIAS  = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

/* ─── Skeleton Loader ──────────────────────────────────────────────────────── */
const SkeletonCell = () => (
  <div style={{
    borderRadius: 10, minHeight: 72, background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
  }} />
);

const SkeletonCard = () => (
  <div style={{ background:"#fff", border:"1px solid #F3F4F6", borderRadius:14, padding:18, marginBottom:12 }}>
    {[80,120,60].map((w,i) => (
      <div key={i} style={{ height:12, width:`${w}%`, borderRadius:6, marginBottom:10,
        background:"linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
        backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
    ))}
  </div>
);

/* ─── Month Picker Moderno ─────────────────────────────────────────────────── */
const MonthPickerModern = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(value.year());

  const handleSelect = (monthIdx) => {
    onChange(dayjs(`${viewYear}-${String(monthIdx + 1).padStart(2, "0")}-01`));
    setOpen(false);
  };

  const isSelected = (monthIdx) =>
    value.year() === viewYear && value.month() === monthIdx;

  const isCurrentMonth = (monthIdx) =>
    dayjs().year() === viewYear && dayjs().month() === monthIdx;

  return (
    <div style={{ position: "relative" }}>
      {/* Trigger Button */}
      <button
        onClick={() => { setOpen(!open); setViewYear(value.year()); }}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "9px 16px", borderRadius: 10, cursor: "pointer",
          background: open ? "#EFF6FF" : "#fff",
          border: open ? "1.5px solid #3B82F6" : "1.5px solid #E5E7EB",
          boxShadow: open ? "0 0 0 3px rgba(59,130,246,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
          transition: "all 0.18s ease", minWidth: 170,
          color: "#111827",
        }}
      >
        <span style={{
          width: 28, height: 28, borderRadius: 7,
          background: "linear-gradient(135deg, #185FA5, #2563EB)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <CalendarOutlined style={{ fontSize: 13, color: "#fff" }} />
        </span>
        <div style={{ textAlign: "left", flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
            {MESES[value.month()]}
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.2 }}>{value.year()}</div>
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
          transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s",
          color: "#9CA3AF",
        }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 1000,
            background: "#fff", borderRadius: 14, padding: 16,
            border: "1px solid #E5E7EB",
            boxShadow: "0 16px 48px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)",
            width: 260,
            animation: "dropdownFadeIn 0.16s ease",
          }}>
            {/* Header do ano */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #F3F4F6",
            }}>
              <button
                onClick={() => setViewYear(y => y - 1)}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: "1px solid #E5E7EB",
                  background: "#F9FAFB", cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center", color: "#374151",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#F9FAFB"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
              >
                <LeftOutlined style={{ fontSize: 10 }} />
              </button>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", letterSpacing: "-0.3px" }}>
                {viewYear}
              </span>
              <button
                onClick={() => setViewYear(y => y + 1)}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: "1px solid #E5E7EB",
                  background: "#F9FAFB", cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center", color: "#374151",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#F9FAFB"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
              >
                <RightOutlined style={{ fontSize: 10 }} />
              </button>
            </div>

            {/* Grid de meses */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {MESES_CURTOS.map((m, i) => {
                const selected = isSelected(i);
                const current = isCurrentMonth(i);
                return (
                  <button
                    key={m}
                    onClick={() => handleSelect(i)}
                    style={{
                      padding: "8px 4px", borderRadius: 9, fontSize: 12, fontWeight: selected ? 700 : 500,
                      cursor: "pointer", border: selected ? "none" : current ? "1.5px solid #BFDBFE" : "1px solid transparent",
                      background: selected
                        ? "linear-gradient(135deg, #185FA5, #2563EB)"
                        : current ? "#EFF6FF" : "transparent",
                      color: selected ? "#fff" : current ? "#1D4ED8" : "#374151",
                      transition: "all 0.15s",
                      boxShadow: selected ? "0 2px 8px rgba(37,99,235,0.35)" : "none",
                    }}
                    onMouseEnter={e => { if (!selected) { e.currentTarget.style.background = "#F3F4F6"; e.currentTarget.style.borderColor = "#E5E7EB"; } }}
                    onMouseLeave={e => { if (!selected) { e.currentTarget.style.background = current ? "#EFF6FF" : "transparent"; e.currentTarget.style.borderColor = current ? "#BFDBFE" : "transparent"; } }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Status Badge ─────────────────────────────────────────────────────────── */
const StatusBadge = ({ justificado }) => {
  const cfg = getFaltaStatus(justificado);
  const icons = {
    S: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <circle cx="5.5" cy="5.5" r="5.5" fill={cfg.dot}/>
        <path d="M3 5.5l2 2 3-3" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    N: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <circle cx="5.5" cy="5.5" r="5.5" fill={cfg.dot}/>
        <path d="M3.5 3.5l4 4M7.5 3.5l-4 4" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    P: (
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <circle cx="5.5" cy="5.5" r="5.5" fill={cfg.dot}/>
        <path d="M5.5 3v2.5l1.5 1.5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
  };
  const key = justificado === "S" ? "S" : justificado === "N" ? "N" : "P";

  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px 4px 7px", borderRadius: 20,
      background: cfg.badgeBg, border: `1px solid ${cfg.border}`,
      fontSize: 11, fontWeight: 700, color: cfg.color,
      letterSpacing: "0.2px", textTransform: "uppercase",
    }}>
      {icons[key]}
      {cfg.label}
    </span>
  );
};

/* ─── Falta Card ───────────────────────────────────────────────────────────── */
const FaltaCard = ({ f, onClick }) => {
  const cfg = getFaltaStatus(f.justificado);
  const dI = dayjs(f.inicio), dF = dayjs(f.fim);
  const durHours = dF.diff(dI, "hour");
  const durMin = dF.diff(dI, "minute") % 60;
  const sameDay = dI.format("DD/MM/YYYY") === dF.format("DD/MM/YYYY");

  return (
    <div
      className="falta-card-item"
      onClick={onClick}
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 14,
        padding: "0",
        marginBottom: 10,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        cursor: "pointer",
        overflow: "hidden",
        transition: "all 0.18s ease",
      }}
    >
      {/* Faixa colorida lateral + topo */}
      <div style={{
        height: 4,
        background: `linear-gradient(90deg, ${cfg.cardBorder}, ${cfg.dot}80)`,
      }} />

      <div style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          {/* Left: motivo + id */}
          <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 3, lineHeight: 1.3 }}>
              {f.motivo || "Sem motivo indicado"}
            </div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>
              #{f.id} &middot; {f.colaborador}
            </div>
          </div>
          {/* Right: badge */}
          <StatusBadge justificado={f.justificado} />
        </div>

        {/* Info row */}
        <div style={{
          display: "flex", gap: 6, flexWrap: "wrap",
          paddingTop: 12, borderTop: "1px solid #F9FAFB",
        }}>
          {/* Data */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#F9FAFB", borderRadius: 8, padding: "5px 10px",
            border: "1px solid #F3F4F6",
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="2" width="10" height="9" rx="1.5" stroke="#9CA3AF" strokeWidth="1.2"/>
              <line x1="1" y1="5" x2="11" y2="5" stroke="#9CA3AF" strokeWidth="1.2"/>
              <line x1="4" y1="1" x2="4" y2="3" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="8" y1="1" x2="8" y2="3" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>
              {dI.format("DD/MM/YYYY")}
              {!sameDay && ` → ${dF.format("DD/MM/YYYY")}`}
            </span>
          </div>

          {/* Horário */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#F9FAFB", borderRadius: 8, padding: "5px 10px",
            border: "1px solid #F3F4F6",
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="5" stroke="#9CA3AF" strokeWidth="1.2"/>
              <path d="M6 3v3l2 1.5" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span style={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>
              {dI.format("HH:mm")} – {dF.format("HH:mm")}
            </span>
          </div>

          {/* Duração */}
          {(durHours > 0 || durMin > 0) && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#F9FAFB", borderRadius: 8, padding: "5px 10px",
              border: "1px solid #F3F4F6",
            }}>
              <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 600 }}>
                {durHours > 0 ? `${durHours}h` : ""}{durMin > 0 ? ` ${durMin}m` : ""}
              </span>
            </div>
          )}

          {/* Aprovado por */}
          {f.justificado === "S" && f.justificador && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#ECFDF5", borderRadius: 8, padding: "5px 10px",
              border: "1px solid #A7F3D0", marginLeft: "auto",
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="4" r="2.5" stroke="#059669" strokeWidth="1.2"/>
                <path d="M1.5 11c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5" stroke="#059669" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: 11, color: "#065F46", fontWeight: 600 }}>{f.justificador}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Stats de Faltas ──────────────────────────────────────────────────────── */
const FaltasStats = ({ data }) => {
  const aceites   = data.filter(f => f.justificado === "S").length;
  const rejeitados = data.filter(f => f.justificado === "N").length;
  const pendentes = data.filter(f => f.justificado !== "S" && f.justificado !== "N").length;

  const stats = [
    { label: "Total", value: data.length, color: "#185FA5", bg: "#EFF6FF", border: "#BFDBFE" },
    { label: "Aceites", value: aceites, color: "#065F46", bg: "#ECFDF5", border: "#A7F3D0" },
    { label: "Pendentes", value: pendentes, color: "#92400E", bg: "#FFFBEB", border: "#FDE68A" },
    { label: "Rejeitados", value: rejeitados, color: "#9F1239", bg: "#FFF1F2", border: "#FECDD3" },
  ];

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
      {stats.map(s => (
        <div key={s.label} style={{
          flex: "1 1 70px", background: s.bg,
          border: `1px solid ${s.border}`,
          borderRadius: 12, padding: "12px 16px",
          transition: "all 0.2s",
        }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
          <div style={{ fontSize: 11, color: s.color, marginTop: 3, fontWeight: 600, opacity: 0.8 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
};

/* ─── Componente Principal ─────────────────────────────────────────────────── */
const Transacoes = () => {
  const userStored = JSON.parse(localStorage.getItem("user") || "{}");
  const employeeId = userStored.employee_id;
  const diretor    = userStored.diretor || "N";
  const nomeUser   = userStored.name || "Colaborador";
  const [faltaSelecionada, setFaltaSelecionada] = useState(null);
  const [modalFaltaOpen, setModalFaltaOpen] = useState(false);

  const [tab, setTab]               = useState("calendario");
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [calData, setCalData]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [modalOpen, setModalOpen]   = useState(false);
  const [tipos, setTipos]           = useState([]);
  const [form]                      = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList]     = useState([]);

  const [faltasData, setFaltasData]       = useState([]);
  const [faltasMes, setFaltasMes]         = useState(dayjs());
  const [faltaFiltro, setFaltaFiltro]     = useState("TODOS");
  const [faltasLoading, setFaltasLoading] = useState(false);

  useEffect(() => { document.title = "Movimentações | Dashboard"; }, []);

  /* ── Calendário ── */
  const loadCalendar = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    const dataInicio = currentDate.startOf("month").format("YYYY-MM-DD");
    const dataFim    = currentDate.endOf("month").format("YYYY-MM-DD");
    try {
      const res = await Api.ListTransacao({ employee_id: employeeId, dataInicio, dataFim });
      setCalData(res?.data?.registros || []);
    } catch { toast.error("Erro ao carregar dados."); }
    finally  { setLoading(false); }
  }, [employeeId, currentDate]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const byDate = Object.fromEntries(calData.map(d => [d.data, d]));

  const stats = [
    { key: "PRESENTE",           label: "Presenças" },
    { key: "ATRASO",             label: "Atrasos" },
    { key: "FALTA",              label: "Faltas" },
    { key: "FALTA JUSTIFICADA",  label: "Justificadas" },
    { key: "FÉRIAS",             label: "Férias" },
    { key: "FERIADO",            label: "Feriados" },
  ].map(s => ({
    ...s,
    cfg: STATUS_CONFIG[s.key],
    value: calData.filter(d => d.status === s.key).length,
  }));

  /* ── Detalhe do dia ── */
  const openDay = async (dateStr) => {
    const rec = byDate[dateStr] || { data: dateStr, status: "SEM DADOS" };
    setSelectedDay(rec);
    setFileList([]);
    setModalOpen(true);
    form.resetFields();
    form.setFieldsValue({ inicio: dayjs("08:30","HH:mm"), fim: dayjs("17:30","HH:mm") });
    if (rec.status === "FALTA" && !tipos.length) {
      try { const t = await Api.ListTipos(); setTipos(t || []); }
      catch { setTipos([]); }
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    onRemove: () => setFileList([]),
    fileList,
    maxCount: 1,
  };

  /* ── Submeter justificação ── */
  const submitJustificacao = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    const fd = new FormData();
    fd.append("direcao", userStored.direcao);
    fd.append("employee_id", employeeId);
    fd.append("diretor",     diretor);
    fd.append("inicio",      `${selectedDay.data} ${values.inicio.format("HH:mm:ss")}`);
    fd.append("fim",         `${selectedDay.data} ${values.fim.format("HH:mm:ss")}`);
    fd.append("id_tipo",     values.id_tipo);
    if (values.obs) fd.append("obs", values.obs);
    if (fileList[0]) {
      fd.append("file", fileList[0]);
    }
    try {
      await Api.AddFalta(fd);
      toast.success("Justificação enviada com sucesso!");
      setModalOpen(false);
      loadCalendar();
    } catch { toast.error("Erro ao enviar justificação."); }
    finally  { setSubmitting(false); }
  };

  /* ── Faltas ── */
  const loadFaltas = useCallback(async () => {
    if (!employeeId) return;
    setFaltasLoading(true);
    const dataInicio = faltasMes.startOf("month").format("YYYY-MM-DD");
    const dataFim    = faltasMes.endOf("month").format("YYYY-MM-DD");
    try {
      const res = await Api.ListFalta({ employee_id: employeeId, dataInicio, dataFim });
      setFaltasData(Array.isArray(res) ? res : []);
    } catch { toast.error("Erro ao carregar faltas."); }
    finally  { setFaltasLoading(false); }
  }, [employeeId, faltasMes]);

  useEffect(() => { if (tab === "faltas") loadFaltas(); }, [tab, loadFaltas]);

  const faltasFiltradas = faltasData.filter(f =>
    faltaFiltro === "TODOS" ? true :
    faltaFiltro === "APROVADO" ? f.justificado === "S" :
    faltaFiltro === "REJEITADO" ? f.justificado === "N" :
    (f.justificado !== "S" && f.justificado !== "N")
  );

  /* ── Render do calendário ── */
  const renderCells = () => {
    const year      = currentDate.year();
    const month     = currentDate.month();
    const firstDow  = dayjs(`${year}-${String(month+1).padStart(2,"0")}-01`).day();
    const totalDays = currentDate.daysInMonth();
    const todayStr  = dayjs().format("YYYY-MM-DD");
    const cells     = [];

    for (let i = 0; i < firstDow; i++) cells.push(<div key={`e${i}`} />);

    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const rec     = byDate[dateStr];
      const cfg     = rec ? STATUS_CONFIG[rec.status] : null;
      const isToday = dateStr === todayStr;
      const isFds   = rec?.status === "FINAL DE SEMANA";

      cells.push(
        <div
          key={dateStr}
          onClick={() => openDay(dateStr)}
          style={{
            borderRadius: 10,
            padding: "9px 8px 8px",
            minHeight: 72,
            cursor: "pointer",
            background: cfg ? cfg.bg : "#FAFAFA",
            border: isToday ? "2px solid #185FA5" : "1px solid transparent",
            opacity: isFds ? 0.55 : 1,
            transition: "all 0.18s ease",
            position: "relative",
          }}
          onMouseEnter={e => { if(!isFds) e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.10)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
        >
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
            <span style={{
              fontSize: 13, fontWeight: isToday ? 700 : 500,
              color: isToday ? "#185FA5" : "#374151",
              background: isToday ? "#DBEAFE" : "transparent",
              borderRadius: isToday ? "50%" : 0,
              width: isToday ? 22 : "auto", height: isToday ? 22 : "auto",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>{d}</span>
            {cfg && <span style={{ width:7, height:7, borderRadius:"50%", background:cfg.dot, flexShrink:0 }} />}
          </div>

          {cfg && !isFds && (
            <span style={{
              fontSize: 9, fontWeight: 600, letterSpacing:"0.3px",
              color: cfg.color, display:"block",
              textTransform:"uppercase",
            }}>{cfg.label}</span>
          )}

          {rec?.entrada && (
            <div style={{ marginTop:5, display:"flex", flexDirection:"column", gap:2 }}>
              <span style={{ fontSize:9, color:"#374151", fontWeight:500 }}>▶ {rec.entrada}</span>
              {rec.saida && <span style={{ fontSize:9, color:"#374151", fontWeight:500 }}>◀ {rec.saida}</span>}
            </div>
          )}

          {rec?.atraso && (
            <span style={{ fontSize:8, fontWeight:600, color:"#9A3412", marginTop:3, display:"block" }}>
               +{rec.atraso}
            </span>
          )}
          {rec?.horas_extras && (
            <span style={{ fontSize:8, fontWeight:600, color:"#1D4ED8", marginTop:1, display:"block" }}>
              {rec.horas_extras}
            </span>
          )}
        </div>
      );
    }
    return cells;
  };

  const rec    = selectedDay;
  const recCfg = rec ? STATUS_CONFIG[rec.status] : null;

  /* ─────────────────── RENDER ─────────────────── */
  return (
    <Layout style={{ background:"transparent", minHeight:"100vh" }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        @keyframes dropdownFadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:none} }
        .trans-tab-btn { transition: all 0.2s; }
        .trans-tab-btn:hover { background: #F0F6FF !important; }
        .trans-stat-card { transition: all 0.2s; }
        .trans-stat-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
        .falta-card-item { transition: all 0.18s; }
        .falta-card-item:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important; border-color: #BFDBFE !important; }
        .filter-chip { transition: all 0.15s; cursor: pointer; }
        .filter-chip:hover { opacity: 0.85; }
      `}</style>

      <Content style={{ padding:"28px 36px", animation:"fadeUp 0.4s ease" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── HEADER PRINCIPAL ─────────────────────────────────────────── */}
        <div style={{
          background:"linear-gradient(135deg, #0F4C8A 0%, #185FA5 50%, #2563EB 100%)",
          borderRadius:18, padding:"26px 32px", marginBottom:28,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          boxShadow:"0 16px 48px rgba(24,95,165,0.28)",
          position:"relative", overflow:"hidden",
        }}>
          <div style={{ position:"absolute", right:-30, top:-30, width:180, height:180, borderRadius:"50%",
            background:"rgba(255,255,255,0.06)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", right:60, bottom:-50, width:120, height:120, borderRadius:"50%",
            background:"rgba(255,255,255,0.04)", pointerEvents:"none" }} />

          <div style={{ position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:36, height:36, borderRadius:10,
                background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <h1 style={{ color:"#fff", fontSize:20, fontWeight:700, margin:0, letterSpacing:"-0.3px" }}>
                Movimentações
              </h1>
            </div>
            <p style={{ color:"rgba(255,255,255,0.70)", fontSize:13, margin:0 }}>
              Olá, <strong style={{color:"rgba(255,255,255,0.92)"}}>{userStored.nome}</strong>
            </p>
          </div>

          <div style={{ display:"flex", gap:4, background:"rgba(0,0,0,0.2)", borderRadius:12, padding:4 }}>
            {[["calendario","Calendário"],["faltas","Minhas Faltas"]].map(([key,label]) => (
              <button key={key} className="trans-tab-btn" onClick={() => setTab(key)} style={{
                padding:"8px 20px", fontSize:13, cursor:"pointer",
                borderRadius:9, border:"none", fontWeight:600, letterSpacing:"0.1px",
                background: tab===key ? "#fff" : "transparent",
                color: tab===key ? "#185FA5" : "rgba(255,255,255,0.80)",
                boxShadow: tab===key ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* ══════════════ ABA CALENDÁRIO ══════════════ */}
        {tab === "calendario" && (
          <>
            <div style={{ display:"flex", gap:16, marginBottom:24, flexWrap:"wrap", alignItems:"stretch" }}>
              <div style={{
                background:"#fff", borderRadius:14, padding:"16px 20px",
                border:"1px solid #E5E7EB", display:"flex", alignItems:"center",
                gap:12, flexShrink:0, boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
              }}>
                <button onClick={() => setCurrentDate(d => d.subtract(1,"month"))} style={{
                  width:34, height:34, borderRadius:8, border:"1px solid #E5E7EB",
                  background:"#F9FAFB", cursor:"pointer", fontSize:16, display:"flex",
                  alignItems:"center", justifyContent:"center", color:"#374151",
                }}>‹</button>
                <div style={{ textAlign:"center", minWidth:120 }}>
                  <div style={{ fontSize:17, fontWeight:700, color:"#111827", letterSpacing:"-0.3px" }}>
                    {MESES[currentDate.month()]}
                  </div>
                  <div style={{ fontSize:12, color:"#9CA3AF", fontWeight:500 }}>{currentDate.year()}</div>
                </div>
                <button onClick={() => setCurrentDate(d => d.add(1,"month"))} style={{
                  width:34, height:34, borderRadius:8, border:"1px solid #E5E7EB",
                  background:"#F9FAFB", cursor:"pointer", fontSize:16, display:"flex",
                  alignItems:"center", justifyContent:"center", color:"#374151",
                }}>›</button>
              </div>

              <div style={{ display:"flex", gap:10, flex:1, flexWrap:"wrap" }}>
                {stats.map(s => (
                  <div key={s.key} className="trans-stat-card" style={{
                    background:"#fff", border:"1px solid #F3F4F6", borderRadius:14,
                    padding:"14px 18px", flex:"1 1 80px", minWidth:80,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
                    borderTop:`3px solid ${s.cfg.dot}`,
                  }}>
                    <div style={{ fontSize:24, fontWeight:700, color:s.cfg.color, lineHeight:1 }}>
                      {loading ? "—" : s.value}
                    </div>
                    <div style={{ fontSize:11, color:"#9CA3AF", marginTop:4, fontWeight:500, letterSpacing:"0.2px" }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background:"#fff", borderRadius:16, padding:20,
              border:"1px solid #E5E7EB", boxShadow:"0 4px 16px rgba(0,0,0,0.05)",
            }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4, marginBottom:8 }}>
                {DIAS.map((d,i) => (
                  <div key={d} style={{
                    textAlign:"center", fontSize:11, fontWeight:700, letterSpacing:"0.5px",
                    color: i===0||i===6 ? "#D1D5DB" : "#9CA3AF",
                    padding:"6px 0", textTransform:"uppercase",
                  }}>{d}</div>
                ))}
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:5 }}>
                {loading
                  ? Array.from({length:35}).map((_,i) => <SkeletonCell key={i}/>)
                  : renderCells()
                }
              </div>

              <div style={{ display:"flex", gap:16, marginTop:18, paddingTop:16,
                borderTop:"1px solid #F3F4F6", flexWrap:"wrap" }}>
                {Object.entries(STATUS_CONFIG).map(([key,cfg]) => (
                  <div key={key} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <span style={{ width:8, height:8, borderRadius:"50%", background:cfg.dot, flexShrink:0 }}/>
                    <span style={{ fontSize:11, color:"#6B7280", fontWeight:500 }}>{cfg.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ══════════════ ABA FALTAS ══════════════ */}
        {tab === "faltas" && (
          <>
            {/* ── Painel de filtros renovado ── */}
            <div style={{
              background: "#fff", borderRadius: 16, padding: "20px 24px",
              border: "1px solid #E5E7EB", marginBottom: 20,
              boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
            }}>
              {/* Título da secção */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 3, height: 20, borderRadius: 2, background: "linear-gradient(180deg,#185FA5,#2563EB)" }} />
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Minhas Faltas</span>
                </div>
                <span style={{
                  background: "#EFF6FF", color: "#1D4ED8", fontSize: 12,
                  fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                  border: "1px solid #BFDBFE",
                }}>
                  {faltasFiltradas.length} registo{faltasFiltradas.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Linha de filtros */}
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                {/* Month Picker moderno */}
                <MonthPickerModern value={faltasMes} onChange={setFaltasMes} />

                {/* Divisor */}
                <div style={{ width: 1, height: 32, background: "#E5E7EB", flexShrink: 0 }} />

                {/* Chips de filtro */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    { key: "TODOS", label: "Todos", color: "#374151", bg: "#F3F4F6", activeBg: "#1E40AF", activeColor: "#fff" },
                    { key: "APROVADO", label: "Aceites", color: "#065F46", bg: "#ECFDF5", activeBg: "#065F46", activeColor: "#fff", border: "#A7F3D0" },
                    { key: "PENDENTE", label: "Pendentes", color: "#92400E", bg: "#FFFBEB", activeBg: "#92400E", activeColor: "#fff", border: "#FDE68A" },
                    { key: "REJEITADO", label: "Rejeitados", color: "#9F1239", bg: "#FFF1F2", activeBg: "#9F1239", activeColor: "#fff", border: "#FECDD3" },
                  ].map(chip => {
                    const active = faltaFiltro === chip.key;
                    return (
                      <button
                        key={chip.key}
                        className="filter-chip"
                        onClick={() => setFaltaFiltro(chip.key)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                          border: active ? "none" : `1.5px solid ${chip.border || "#E5E7EB"}`,
                          background: active ? chip.activeBg : chip.bg,
                          color: active ? chip.activeColor : chip.color,
                          boxShadow: active ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.15s",
                        }}
                      >
                        {chip.key !== "TODOS" && (
                          <span style={{
                            width: 7, height: 7, borderRadius: "50%",
                            background: active ? "rgba(255,255,255,0.7)" :
                              chip.key === "APROVADO" ? "#10B981" :
                              chip.key === "PENDENTE" ? "#F59E0B" : "#F43F5E",
                            flexShrink: 0,
                          }} />
                        )}
                        {chip.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Stats mini */}
            {!faltasLoading && faltasData.length > 0 && (
              <FaltasStats data={faltasData} />
            )}

            {/* Lista de faltas */}
            {faltasLoading ? (
              <>{[1,2,3].map(i => <SkeletonCard key={i}/>)}</>
            ) : faltasFiltradas.length === 0 ? (
              <div style={{
                background: "#fff", borderRadius: 16, padding: "56px 24px", textAlign: "center",
                border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: "50%", background: "#F3F4F6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px", fontSize: 28,
                }}>📋</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                  Nenhuma falta encontrada
                </div>
                <div style={{ fontSize: 13, color: "#9CA3AF" }}>
                  Tente ajustar os filtros ou selecionar outro período
                </div>
              </div>
            ) : (
              <div style={{ animation: "fadeUp 0.3s ease" }}>
                {faltasFiltradas.map(f => (
                  <FaltaCard
                    key={f.id}
                    f={f}
                    onClick={() => { setFaltaSelecionada(f); setModalFaltaOpen(true); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ══════════════ MODAL DETALHE CALENDÁRIO ══════════════ */}
        <Modal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={460}
          styles={{ header:{ borderBottom:"1px solid #F3F4F6", paddingBottom:14 } }}
          title={
            rec && (
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:38, height:38, borderRadius:10, flexShrink:0,
                  background: recCfg?.bg || "#F3F4F6",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <span style={{ width:12, height:12, borderRadius:"50%", background: recCfg?.dot || "#9CA3AF" }}/>
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:"#111827" }}>
                    {rec.dia_semana ? rec.dia_semana.charAt(0).toUpperCase()+rec.dia_semana.slice(1) : rec.data}
                  </div>
                  <div style={{ fontSize:12, color:"#9CA3AF", fontWeight:400 }}>
                    {rec.data} &middot; <span style={{ color: recCfg?.color }}>{recCfg?.label || rec.status}</span>
                  </div>
                </div>
              </div>
            )
          }
        >
          {rec && (
            <div style={{ paddingTop:4 }}>
              {[
                rec.nome     && ["Colaborador", rec.nome],
                rec.entrada  && ["Entrada",      rec.entrada],
                rec.saida    && ["Saída",         rec.saida],
                rec.atraso    && ["Atraso",      <span style={{color:"#9A3412",fontWeight:700}}> {rec.atraso}</span>],
                rec.horas_extras && ["Horas extras", <span style={{color:"#1D4ED8",fontWeight:700}}> {rec.horas_extras}</span>],
                rec.descricao&& ["Descrição",     rec.descricao],
              ].filter(Boolean).map(([label, val]) => (
                <div key={label} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"10px 0", borderBottom:"1px solid #F9FAFB",
                }}>
                  <span style={{ fontSize:13, color:"#9CA3AF", fontWeight:500 }}>{label}</span>
                  <span style={{ fontSize:13, color:"#111827", fontWeight:600 }}>{val}</span>
                </div>
              ))}

              {["FINAL DE SEMANA","FÉRIAS","FERIADO","PRESENTE","ATRASO","FALTA JUSTIFICADA"].includes(rec.status) && (
                <div style={{ marginTop:16, padding:"12px 16px", borderRadius:10,
                  background:"#F9FAFB", border:"1px solid #F3F4F6" }}>
                  <p style={{ fontSize:13, color:"#9CA3AF", margin:0, textAlign:"center" }}>
                    {rec.status === "PRESENTE" || rec.status === "ATRASO"
                      ? "✓ Dia trabalhado — sem ações disponíveis"
                      : "Nenhuma ação disponível para este dia."}
                  </p>
                </div>
              )}

              {rec.status === "FALTA" && (
                <div style={{ marginTop:20, borderTop:"1px solid #F3F4F6", paddingTop:18 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:3, height:18, borderRadius:2, background:"#185FA5" }}/>
                    <span style={{ fontSize:14, fontWeight:700, color:"#111827" }}>Justificar esta falta</span>
                  </div>

                  <Form form={form} layout="vertical" size="middle">
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                      <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Hora início</span>}
                        name="inicio" rules={[{required:true,message:"Obrigatório"}]}>
                        <TimePicker format="HH:mm" style={{ width:"100%" }}/>
                      </Form.Item>
                      <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Hora fim</span>}
                        name="fim" rules={[{required:true,message:"Obrigatório"}]}>
                        <TimePicker format="HH:mm" style={{ width:"100%" }}/>
                      </Form.Item>
                    </div>

                    <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Motivo</span>}
                      name="id_tipo" rules={[{required:true,message:"Selecione o motivo"}]}>
                      <Select placeholder="Selecione o tipo de falta...">
                        {tipos.map(t => <Option key={t.id} value={t.id}>{t.descricao}</Option>)}
                      </Select>
                    </Form.Item>

                    <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Observação</span>} name="obs">
                      <input placeholder="Descrição opcional..." style={{
                        width:"100%", padding:"8px 12px", borderRadius:8, fontSize:13,
                        border:"1px solid #D1D5DB", outline:"none", color:"#374151",
                        boxSizing:"border-box",
                      }}/>
                    </Form.Item>

                    <Form.Item label={<span style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Comprovativo (PDF)</span>}>
                      <Upload {...uploadProps} accept=".pdf">
                        <Button icon={<UploadOutlined/>}>Selecionar PDF</Button>
                      </Upload>
                    </Form.Item>

                    <Button
                      type="primary" block loading={submitting} onClick={submitJustificacao}
                      style={{
                        height:42, borderRadius:10, fontSize:14, fontWeight:700,
                        background:"linear-gradient(135deg,#185FA5,#2563EB)",
                        border:"none", letterSpacing:"0.2px",
                        boxShadow:"0 4px 14px rgba(24,95,165,0.35)",
                      }}
                    >
                      {submitting ? "A enviar..." : "Enviar justificação"}
                    </Button>
                  </Form>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* ══════════════ MODAL DETALHE FALTA ══════════════ */}
        <Modal
          open={modalFaltaOpen}
          onCancel={() => setModalFaltaOpen(false)}
          footer={null}
          width={520}
          title={null}
          closable
          maskClosable
        >
          {faltaSelecionada && (() => {
            const cfg = getFaltaStatus(faltaSelecionada.justificado);
            return (
              <div style={{ padding: "8px 0 0" }}>
                {/* Header com gradiente de status */}
                <div style={{
                  background: `linear-gradient(135deg, ${cfg.bg}, #fff)`,
                  border: `1px solid ${cfg.border}`,
                  borderRadius: 14, padding: "18px 20px", marginBottom: 20,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: cfg.badgeBg, border: `2px solid ${cfg.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 22,
                    }}>
                      {faltaSelecionada.justificado === "S"
                        ? <CheckCircleFilled style={{ color: "#10B981" }} />
                        : faltaSelecionada.justificado === "N"
                        ? <CloseCircleFilled style={{ color: "#F43F5E" }} />
                        : <ExclamationCircleFilled style={{ color: "#F59E0B" }} />
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                        {faltaSelecionada.motivo || "Sem motivo indicado"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <StatusBadge justificado={faltaSelecionada.justificado} />
                        <span style={{ fontSize: 12, color: "#9CA3AF" }}>#{faltaSelecionada.id}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Informações Gerais */}
                  <SectionBlock title="Informações Gerais">
                    <DetailRow label="Colaborador" icon={<UserOutlined />} value={faltaSelecionada.colaborador} />
                    <DetailRow label="Motivo" icon={<FileTextOutlined />} value={faltaSelecionada.motivo || "—"} />
                  </SectionBlock>

                  {/* Período */}
                  <SectionBlock title="Período">
                    <DetailRow label="Início" icon={<CalendarOutlined />} value={dayjs(faltaSelecionada.inicio).format("DD/MM/YYYY HH:mm")} />
                    <DetailRow label="Fim" icon={<CalendarOutlined />} value={dayjs(faltaSelecionada.fim).format("DD/MM/YYYY HH:mm")} />
                    <DetailRow label="Duração" icon={<ClockCircleOutlined />}
                      value={`${dayjs(faltaSelecionada.fim).diff(dayjs(faltaSelecionada.inicio), 'hour')}h ${dayjs(faltaSelecionada.fim).diff(dayjs(faltaSelecionada.inicio), 'minute') % 60}m`}
                    />
                  </SectionBlock>

                  {/* Aprovação */}
                  {(faltaSelecionada.justificador || faltaSelecionada.data_aprovacao) && (
                    <SectionBlock title="Aprovação" accent={faltaSelecionada.justificado === "S" ? "#10B981" : faltaSelecionada.justificado === "N" ? "#F43F5E" : "#F59E0B"}>
                      {faltaSelecionada.justificador && (
                        <DetailRow label="Por" icon={<UserOutlined />} value={faltaSelecionada.justificador} />
                      )}
                      {faltaSelecionada.data_aprovacao && (
                        <DetailRow label="Data" icon={<CalendarOutlined />}
                          value={dayjs(faltaSelecionada.data_aprovacao).format("DD/MM/YYYY HH:mm")}
                        />
                      )}
                    </SectionBlock>
                  )}

                  {/* Observações */}
                  {(faltaSelecionada.obs || faltaSelecionada.obs2) && (
                    <SectionBlock title="Observações">
                      {faltaSelecionada.obs && (
                        <div style={{ marginBottom: faltaSelecionada.obs2 ? 10 : 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 4 }}>Colaborador</div>
                          <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{faltaSelecionada.obs}</div>
                        </div>
                      )}
                      {faltaSelecionada.obs2 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 4 }}>Diretor</div>
                          <div style={{
                            fontSize: 13, color: "#9F1239", whiteSpace: "pre-wrap", lineHeight: 1.6,
                            background: "#FFF1F2", borderRadius: 8, padding: "8px 12px",
                            border: "1px solid #FECDD3",
                          }}>{faltaSelecionada.obs2}</div>
                        </div>
                      )}
                    </SectionBlock>
                  )}

                  {/* Comprovativo */}
                  {faltaSelecionada.justificativo && (
                    <a
                      href={faltaSelecionada.justificativo}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        padding: "12px 20px", borderRadius: 10, textDecoration: "none",
                        background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)",
                        border: "1.5px solid #BFDBFE", color: "#1D4ED8",
                        fontSize: 13, fontWeight: 700, transition: "all 0.18s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#DBEAFE"; e.currentTarget.style.borderColor = "#93C5FD"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "linear-gradient(135deg, #EFF6FF, #DBEAFE)"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
                    >
                      <FileTextOutlined style={{ fontSize: 16 }} />
                      Ver comprovativo anexado
                    </a>
                  )}
                </div>
              </div>
            );
          })()}
        </Modal>
      </Content>
    </Layout>
  );
};

/* ─── SectionBlock ─────────────────────────────────────────────────────────── */
const SectionBlock = ({ title, children, accent = "#185FA5" }) => (
  <div style={{
    background: "#F9FAFB", borderRadius: 12, padding: "14px 16px",
    border: "1px solid #F3F4F6",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
      <div style={{ width: 3, height: 14, borderRadius: 2, background: accent }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.6px" }}>
        {title}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {children}
    </div>
  </div>
);

/* ─── DetailRow ────────────────────────────────────────────────────────────── */
const DetailRow = ({ label, value, icon }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    {icon && <span style={{ color: "#9CA3AF", fontSize: 13, flexShrink: 0 }}>{icon}</span>}
    <span style={{ fontSize: 13, color: "#6B7280", minWidth: 110, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{value}</span>
  </div>
);

export default Transacoes;